import {
    createContext,
    startTransition,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { exchangeCustomerGrant } from 'utility/requests';

const CUSTOMER_ACCESS_TOKEN_KEY = 'customer_access_token';
const CUSTOMER_ACCESS_EMAIL = 'CUSTOMER_ACCESS_EMAIL';
const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const accessGrant = new URLSearchParams(location.search).get('grant');
    const [session, setSession] = useState(() => {
        const accessToken = localStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY);
        if (!accessToken) localStorage.removeItem(CUSTOMER_ACCESS_EMAIL);
        return {
            status: accessToken ? 'authenticated' : 'unauthenticated',
            accessToken,
            email: accessToken ? localStorage.getItem(CUSTOMER_ACCESS_EMAIL) : null,
            error: null,
            grant: null,
        };
    });
    const currentSession = useRef(session);
    const exchange = useRef(null);

    const updateSession = useCallback((nextSession) => {
        currentSession.current = nextSession;
        setSession(nextSession);
    }, []);

    const expireAccessToken = useCallback(() => {
        exchange.current = null;
        localStorage.removeItem(CUSTOMER_ACCESS_TOKEN_KEY);
        localStorage.removeItem(CUSTOMER_ACCESS_EMAIL);
        updateSession({
            status: 'unauthenticated',
            accessToken: null,
            email: null,
            error: null,
            grant: null,
        });
    }, [updateSession]);

    const signOut = useCallback(() => {
        // Keep the auth update in the same transition as BrowserRouter navigation.
        startTransition(() => {
            expireAccessToken();
            navigate('/tickets', { replace: true, state: null });
        });
    }, [expireAccessToken, navigate]);

    useEffect(() => {
        if (!accessGrant) {
            exchange.current = null;
            if (currentSession.current.status === 'loading') {
                const accessToken = localStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY);
                updateSession({
                    status: accessToken ? 'authenticated' : 'unauthenticated',
                    accessToken,
                    email: accessToken ? localStorage.getItem(CUSTOMER_ACCESS_EMAIL) : null,
                    error: null,
                    grant: null,
                });
            }
            return;
        }

        let active = true;
        // Reuse the in-flight exchange when navigation keeps the same grant.
        if (exchange.current?.grant !== accessGrant) {
            exchange.current = { grant: accessGrant, promise: exchangeCustomerGrant(accessGrant) };
            updateSession({
                status: 'loading',
                accessToken: null,
                email: null,
                error: null,
                grant: accessGrant,
            });
        }

        const pendingExchange = exchange.current;
        async function authenticate() {
            try {
                const response = await pendingExchange.promise;
                const accessToken = response?.accessToken;
                if (typeof accessToken !== 'string' || !accessToken.trim()) {
                    throw new Error('Access token missing from response');
                }
                if (!active || exchange.current !== pendingExchange) return;

                const email = response?.email;
                if (typeof email !== 'string' || !email.trim()) {
                    throw new Error('Customer email missing from response');
                }

                localStorage.setItem(CUSTOMER_ACCESS_TOKEN_KEY, accessToken);
                localStorage.setItem(CUSTOMER_ACCESS_EMAIL, email);
                updateSession({
                    status: 'authenticated',
                    accessToken,
                    email,
                    error: null,
                    grant: accessGrant,
                });
                const params = new URLSearchParams(location.search);
                params.delete('grant');
                navigate(
                    {
                        pathname: location.pathname,
                        search: params.toString() ? `?${params}` : '',
                        hash: location.hash,
                    },
                    { replace: true, state: location.state },
                );
            } catch (error) {
                if (!active || exchange.current !== pendingExchange) return;
                localStorage.removeItem(CUSTOMER_ACCESS_TOKEN_KEY);
                localStorage.removeItem(CUSTOMER_ACCESS_EMAIL);
                updateSession({
                    status: 'error',
                    accessToken: null,
                    email: null,
                    error,
                    grant: accessGrant,
                });
            }
        }

        authenticate();
        return () => {
            active = false;
        };
    }, [accessGrant, location, navigate, updateSession]);

    const customerFetch = useCallback(
        async (path, options = {}) => {
            const base = new URL(
                `${process.env.REACT_APP_API_URL}/customer_api/`,
                window.location.origin,
            );
            const url = new URL(path, base);
            if (url.origin !== base.origin || !url.pathname.startsWith(base.pathname)) {
                throw new Error('Customer requests must stay within the customer API');
            }
            const requestSession = currentSession.current;
            if (requestSession.status !== 'authenticated' || !requestSession.accessToken) {
                throw new Error('Customer session is not authenticated');
            }
            const headers = new Headers(options.headers);
            headers.set('x-store-access-token', requestSession.accessToken);
            const response = await fetch(url.href, {
                ...options,
                headers: Object.fromEntries(headers),
            });
            if (response.status === 401 || response.status === 403) {
                if (currentSession.current === requestSession) expireAccessToken();
                throw new Error('Customer session expired');
            }
            return response;
        },
        [expireAccessToken],
    );

    // Block children immediately on a new grant, before its effect starts.
    const status =
        accessGrant && (session.grant !== accessGrant || exchange.current?.grant !== accessGrant)
            ? 'loading'
            : session.status;
    return (
        <CustomerAuthContext.Provider
            value={{
                status,
                error: session.error,
                accessToken: session.accessToken,
                email: status === 'authenticated' ? session.email : null,
                signOut,
                expireAccessToken,
                customerFetch,
            }}
        >
            {children}
        </CustomerAuthContext.Provider>
    );
}

export function useCustomerAuth() {
    const context = useContext(CustomerAuthContext);
    if (!context) throw new Error('useCustomerAuth requires CustomerAuthProvider');
    return context;
}
